import jsPDF from "jspdf";
import { format } from "date-fns";
import { FacilityBooking } from "@/types/facility/booking";
import { convert24HourTo12Hour, displayDate } from "@/lib/helper";

interface PDFExportOptions {
  data: FacilityBooking[];
  filename?: string;
  facilityName?: string;
  date?: string;
}

// Helper to truncate text
const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + "...";
};

export const exportBookingsToPDF = async (options: PDFExportOptions) => {
  const { data, filename = "booking-report.pdf", facilityName, date } = options;

  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 5; 
  let yPosition = margin;

  // ========== HEADER ==========
  
  // Title (Left)
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Booking Report", margin, yPosition + 6);

  // Generated Info (Right)
  const now = new Date();
  const generatedText = `Generated: ${format(now, "MMM dd, yyyy HH:mm")}`;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(generatedText, pageWidth - margin, yPosition + 6, { align: "right" });

  // Filter Info (Below Title)
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  
  const filterTextParts = [];
  if (facilityName) filterTextParts.push(`Facility: ${facilityName}`);
  if (date) filterTextParts.push(`Date: ${date}`);
  const filterText = filterTextParts.join(" | ");
  
  if (filterText) {
    doc.text(filterText, margin, yPosition + 12);
  }

  // Start table closer to header
  yPosition = 20; 

  // ========== TABLE HEADERS ==========
  
  const headers = ["Customer Name", "Facility Name", "Booking Timings", "Date", "Max Att."];
  const colWidths = [55, 55, 45, 30, 15]; 
  
  // Header Style
  doc.setFillColor(255, 255, 255); 
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  
  let xPos = margin;
  headers.forEach((header, i) => {
     doc.text(header, xPos + 2, yPosition + 5);
     xPos += colWidths[i];
  });
  
  // Header Bottom Line
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition + 7, pageWidth - margin, yPosition + 7);

  yPosition += 9;

  // ========== TABLE ROWS ==========
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);

  data.forEach((booking, index) => {
    // Page Break Check
    if (yPosition > pageHeight - 15) {
      doc.addPage();
      yPosition = margin + 5;
      
      // Re-draw Header
      doc.setFont("helvetica", "bold");
      let hx = margin;
      headers.forEach((header, i) => {
        doc.text(header, hx + 2, yPosition + 5);
        hx += colWidths[i];
      });
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition + 7, pageWidth - margin, yPosition + 7);
      
      yPosition += 9;
      
      doc.setFont("helvetica", "normal");
    }

    // Row Background
    if (index % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, yPosition - 1, pageWidth - (margin * 2), 8, "F");
    }

    let timeRange = "-";

    if (booking.slot_start_time && booking.slot_end_time) {
      const startTime = format(new Date(booking.slot_start_time), "hh:mm a");
      const endTime = format(new Date(booking.slot_end_time), "hh:mm a");
      timeRange = `${startTime} - ${endTime}`;
    } else if (booking.start_time && booking.end_time) {
      const startTimeResponse = booking.start_time || "";
      const endTimeResponse = booking.end_time || "";
      timeRange = `${convert24HourTo12Hour(startTimeResponse)} - ${convert24HourTo12Hour(endTimeResponse)}`;
    }
    const dateStr = displayDate(booking.booking_date) || "-";

    const rowData = [
      truncateText(booking.member_name || "-", 30),
      truncateText(booking.facility_name || "-", 30),
      timeRange,
      dateStr,
      booking.max_attendees?.toString() || "0"
    ];

    let cx = margin;
    rowData.forEach((text, i) => {
      doc.text(text, cx + 2, yPosition + 3.5);
      cx += colWidths[i];
    });

    yPosition += 8;
  });

  doc.save(filename);
};
